import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Project } from '@/module/Author/Project';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { CriteriaProjectSelectAddModal } from '@/module/Laboratory/criteria/components/CriteriaProjectSelectAddModal';
import { getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaHeaderButtons } from './components/components/CriteriaHeaderButtons';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';
import { useSaveCriteriaData } from './components/hooks/useSaveCriteriaData';

export interface CenterReportProjectSelectProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportProjectSelect({ labId, criteriaCode }: CenterReportProjectSelectProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: {
      completed,
      projects: { available, selected },
    },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onAdd = (project: Project) => onSubmit({ toAdd: project });
  const onRemove = (project: Project) => onSubmit({ toRemove: project });

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaProjectSelectAddModal
          available={available}
          selected={selected}
          close={close}
          onAdd={onAdd}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {selected.length > 0 && (
            <Stack>
              {selected.map((project, index) => (
                <Flex
                  align="center"
                  key={project.id}
                >
                  {!completed && (
                    <CriteriaItemDeleteButton
                      index={index}
                      onRemove={() => onRemove(project)}
                    />
                  )}

                  <ProjectCard
                    project={project}
                    onClick={() => window.open(`/author/project/${project.id}`)}
                  />
                </Flex>
              ))}
            </Stack>
          )}
          {selected.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
